import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    BadgeEuro,
    ArrowLeft,
    Loader2,
    Plus,
    Pencil,
    Trash2,
    Save,
    X,
    Users,
    EyeOff,
    Eye,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

/**
 * Admin CRUD UI for tariffs (Phase 3.16).
 *
 * Lists all tariffs (incl. inactive) with the number of dealers currently
 * assigned to each. Lets the admin create, edit (in-place) and delete
 * tariffs. Real billing/Stripe is intentionally NOT wired yet — this is the
 * scaffold so the catalog can be maintained while the Pro flow is being
 * planned.
 */
const EMPTY = {
    slug: "",
    name: "",
    description: "",
    monthly_price_cents: 0,
    currency: "EUR",
    features: [],
    is_active: true,
    sort_order: 100,
};

const euros = (cents, currency = "EUR") => {
    const v = (Number(cents) || 0) / 100;
    try {
        return new Intl.NumberFormat("de-DE", { style: "currency", currency, minimumFractionDigits: v % 1 === 0 ? 0 : 2 }).format(v);
    } catch {
        return `${v.toFixed(2)} ${currency}`;
    }
};

export default function AdminTariffsPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState(EMPTY);
    const [showCreate, setShowCreate] = useState(false);
    const [createDraft, setCreateDraft] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (authLoading) return;
        if (!user || user.role !== "admin") navigate("/");
    }, [user, authLoading, navigate]);

    const load = async () => {
        try {
            const r = await api.get("/admin/tariffs");
            setItems(r.data?.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Tarife konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const totalRevenue = useMemo(() => {
        return items.reduce((s, t) => s + (Number(t.assigned_count) || 0) * (Number(t.monthly_price_cents) || 0), 0);
    }, [items]);

    const startEdit = (t) => {
        setEditingId(t.id);
        setDraft({
            slug: t.slug,
            name: t.name,
            description: t.description || "",
            monthly_price_cents: t.monthly_price_cents,
            currency: t.currency || "EUR",
            features: t.features || [],
            is_active: !!t.is_active,
            sort_order: t.sort_order ?? 100,
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft(EMPTY);
    };

    const saveEdit = async () => {
        if (!editingId) return;
        setSaving(true);
        try {
            const payload = { ...draft };
            delete payload.slug; // slug not updatable post-create
            payload.monthly_price_cents = Math.round(Number(payload.monthly_price_cents) || 0);
            payload.sort_order = Math.round(Number(payload.sort_order) || 100);
            await api.patch(`/admin/tariffs/${editingId}`, payload);
            toast.success("Tarif aktualisiert");
            cancelEdit();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const saveCreate = async () => {
        const slug = (createDraft.slug || "").trim().toLowerCase();
        if (!/^[a-z0-9_-]+$/.test(slug)) {
            toast.error("Slug darf nur a-z, 0-9, -, _ enthalten");
            return;
        }
        if (!createDraft.name.trim()) {
            toast.error("Name erforderlich");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/tariffs", {
                ...createDraft,
                slug,
                monthly_price_cents: Math.round(Number(createDraft.monthly_price_cents) || 0),
                sort_order: Math.round(Number(createDraft.sort_order) || 100),
            });
            toast.success("Tarif erstellt");
            setShowCreate(false);
            setCreateDraft(EMPTY);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const deleteTariff = async (t) => {
        if (t.assigned_count > 0) {
            toast.error(`Tarif ist noch ${t.assigned_count} Händlern zugewiesen — bitte zuerst umziehen.`);
            return;
        }
        if (!window.confirm(`Tarif "${t.name}" wirklich löschen?`)) return;
        try {
            await api.delete(`/admin/tariffs/${t.id}`);
            toast.success("Tarif gelöscht");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    if (authLoading || !user || user.role !== "admin") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="admin-tariffs-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Link to="/dashboard" className="text-gray-500 hover:text-[#111827]">
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <BadgeEuro className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">Tarif-Verwaltung</h1>
                        <p className="text-sm text-gray-600">
                            Preisliste pflegen, Tarife erstellen, abrechnen.
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => { setShowCreate(true); setCreateDraft(EMPTY); }}
                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm"
                    data-testid="admin-tariff-create-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Neuer Tarif
                </Button>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-tariffs-kpis">
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Tarife</div>
                    <div className="text-3xl font-black font-mono-num" data-testid="kpi-tariff-count">{items.length}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Zugewiesene Händler</div>
                    <div className="text-3xl font-black font-mono-num" data-testid="kpi-assigned-total">
                        {items.reduce((s, t) => s + (Number(t.assigned_count) || 0), 0)}
                    </div>
                </div>
                <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-4">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Berechneter Monatsumsatz</div>
                    <div className="text-3xl font-black font-mono-num text-[#111827]" data-testid="kpi-mrr">
                        {euros(totalRevenue)}
                    </div>
                    <div className="text-[10px] text-amber-700">aus aktiven Zuweisungen — ohne Mehrwertsteuer</div>
                </div>
            </div>

            {/* Create form */}
            {showCreate && (
                <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-6" data-testid="admin-tariff-create-form">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display font-bold text-lg">Neuen Tarif erstellen</h2>
                        <button onClick={() => setShowCreate(false)} className="text-gray-500 hover:text-[#111827]" aria-label="Schließen">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <TariffFormFields value={createDraft} onChange={setCreateDraft} isCreate />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm font-bold uppercase tracking-wider">Abbrechen</Button>
                        <Button onClick={saveCreate} disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm" data-testid="admin-tariff-create-save">
                            {saving ? "Erstellt…" : "Tarif erstellen"}
                        </Button>
                    </div>
                </div>
            )}

            {/* List */}
            <div className="bg-white border border-gray-200 rounded-sm" data-testid="admin-tariffs-list">
                {loading ? (
                    <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-500" data-testid="admin-tariffs-empty">Noch keine Tarife angelegt.</div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {items.map((t) => (
                            <li key={t.id} className="p-5" data-testid={`admin-tariff-row-${t.slug}`}>
                                {editingId === t.id ? (
                                    <div>
                                        <div className="flex items-baseline gap-2 mb-3">
                                            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Slug</span>
                                            <code className="text-xs font-mono-num text-gray-700">{t.slug}</code>
                                            <span className="text-[10px] text-gray-400">(nicht änderbar nach Erstellung)</span>
                                        </div>
                                        <TariffFormFields value={draft} onChange={setDraft} />
                                        <div className="flex justify-end gap-2 mt-4">
                                            <Button variant="outline" onClick={cancelEdit} className="rounded-sm font-bold uppercase tracking-wider">Abbrechen</Button>
                                            <Button onClick={saveEdit} disabled={saving} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold uppercase tracking-wider rounded-sm" data-testid={`admin-tariff-save-${t.slug}`}>
                                                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Speichert…" : "Speichern"}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-4 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-display font-bold text-xl">{t.name}</h3>
                                                <code className="text-[10px] font-mono-num text-gray-500">{t.slug}</code>
                                                {!t.is_active && (
                                                    <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100 text-[10px]"><EyeOff className="w-2.5 h-2.5 mr-1" /> Inaktiv</Badge>
                                                )}
                                            </div>
                                            {t.description && <p className="text-xs text-gray-600 mt-1">{t.description}</p>}
                                            {t.features?.length > 0 && (
                                                <ul className="mt-2 text-xs text-gray-700 list-disc list-inside" data-testid={`admin-tariff-features-${t.slug}`}>
                                                    {t.features.map((f, i) => <li key={i}>{f}</li>)}
                                                </ul>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-black font-mono-num text-[#FFCC00]" data-testid={`admin-tariff-price-${t.slug}`}>
                                                {euros(t.monthly_price_cents, t.currency)}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider text-gray-500">pro Monat</div>
                                            <div className="text-[11px] text-gray-600 mt-2 flex items-center gap-1 justify-end" data-testid={`admin-tariff-assigned-${t.slug}`}>
                                                <Users className="w-3 h-3" /> {t.assigned_count || 0} Händler
                                            </div>
                                            <div className="flex gap-1 mt-3">
                                                <button onClick={() => startEdit(t)} className="text-xs font-bold uppercase tracking-wider text-[#111827] hover:text-[#FFCC00] inline-flex items-center gap-1 px-2 py-1" data-testid={`admin-tariff-edit-${t.slug}`}>
                                                    <Pencil className="w-3 h-3" /> Bearbeiten
                                                </button>
                                                <button onClick={() => deleteTariff(t)} disabled={(t.assigned_count || 0) > 0} className="text-xs font-bold uppercase tracking-wider text-red-700 hover:text-red-900 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 px-2 py-1" data-testid={`admin-tariff-delete-${t.slug}`}>
                                                    <Trash2 className="w-3 h-3" /> Löschen
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Dealer ↔ Tariff assignment */}
            <DealerAssignments tariffs={items} onChanged={load} />
        </div>
    );
}


// ============================================================================
// Dealer ↔ Tariff assignments — admin lists all B2B dealers and reassigns
// them via a dropdown (Stufe 2b — no real billing yet).
// ============================================================================
function DealerAssignments({ tariffs, onChanged }) {
    const [dealers, setDealers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [updatingId, setUpdatingId] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/admin/dealers/with-tariffs");
            setDealers(r.data?.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Händler konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return dealers;
        return dealers.filter((d) => {
            const hay = `${d.company_name || ""} ${d.name || ""} ${d.email || ""} ${d.city || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [dealers, query]);

    const assign = async (dealer, tariffId) => {
        setUpdatingId(dealer.id);
        try {
            await api.post(`/admin/users/${dealer.id}/tariff`, { tariff_id: tariffId || null });
            const newTariff = tariffs.find((t) => t.id === tariffId) || null;
            setDealers((prev) => prev.map((d) => d.id === dealer.id
                ? { ...d, tariff_id: tariffId || null, tariff: newTariff }
                : d
            ));
            toast.success(tariffId ? `Tarif "${newTariff?.name || tariffId}" zugewiesen` : "Tarif entfernt");
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
        } finally {
            setUpdatingId(null);
        }
    };

    const counts = useMemo(() => {
        const total = dealers.length;
        const assigned = dealers.filter((d) => d.tariff_id).length;
        return { total, assigned, missing: total - assigned };
    }, [dealers]);

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="admin-dealer-assignments">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#FFCC00]" /> Händler-Zuweisungen
                    </h2>
                    <p className="text-xs text-gray-600">
                        {counts.assigned} von {counts.total} Händlern haben einen Tarif zugewiesen
                        {counts.missing > 0 && <span className="text-amber-700"> · {counts.missing} ohne Tarif</span>}
                    </p>
                </div>
                <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Händler suchen…"
                    className="rounded-sm w-full sm:w-72"
                    data-testid="admin-assignments-search"
                />
            </div>
            {loading ? (
                <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
            ) : filtered.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500" data-testid="admin-assignments-empty">
                    {dealers.length === 0 ? "Keine Händler gefunden." : "Keine Treffer für die Suche."}
                </div>
            ) : (
                <ul className="divide-y divide-gray-100" data-testid="admin-assignments-list">
                    {filtered.map((d) => (
                        <li key={d.id} className="px-5 py-3 flex items-center gap-4 flex-wrap" data-testid={`admin-assign-row-${d.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-[#111827] truncate">{d.company_name || d.name || d.email}</div>
                                <div className="text-[11px] text-gray-500 truncate">
                                    {d.email}{d.city ? ` · ${d.city}` : ""}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Aktueller Tarif</div>
                                <div className="text-sm font-bold" data-testid={`admin-assign-current-${d.id}`}>
                                    {d.tariff?.name ? (
                                        <>
                                            {d.tariff.name}
                                            <span className="text-gray-500 font-mono-num text-[11px] ml-1">
                                                ({euros(d.tariff.monthly_price_cents, d.tariff.currency)})
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-amber-700">— kein Tarif —</span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <select
                                    value={d.tariff_id || ""}
                                    onChange={(e) => assign(d, e.target.value || null)}
                                    disabled={updatingId === d.id}
                                    className="h-9 text-sm rounded-sm border border-gray-200 px-2 bg-white min-w-[14rem]"
                                    data-testid={`admin-assign-select-${d.id}`}
                                >
                                    <option value="">— kein Tarif —</option>
                                    {tariffs.filter((t) => t.is_active || t.id === d.tariff_id).map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} ({euros(t.monthly_price_cents, t.currency)})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function TariffFormFields({ value, onChange, isCreate = false }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isCreate && (
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Slug (intern, nicht änderbar)</Label>
                    <Input value={value.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="z.B. premium" className="rounded-sm mt-1 font-mono-num" data-testid="admin-tariff-slug-input" />
                </div>
            )}
            <div className={isCreate ? "" : "md:col-span-2"}>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Name (für Händler sichtbar)</Label>
                <Input value={value.name} onChange={(e) => set("name", e.target.value)} placeholder="z.B. Premium" className="rounded-sm mt-1" data-testid="admin-tariff-name-input" />
            </div>
            <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Beschreibung</Label>
                <Textarea value={value.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="rounded-sm mt-1 text-sm" data-testid="admin-tariff-description-input" />
            </div>
            <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Preis pro Monat (€)</Label>
                <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(Number(value.monthly_price_cents) || 0) / 100}
                    onChange={(e) => set("monthly_price_cents", Math.round((parseFloat(e.target.value) || 0) * 100))}
                    className="rounded-sm mt-1 font-mono-num"
                    data-testid="admin-tariff-price-input"
                />
                <div className="text-[10px] text-gray-500 mt-1">Wird intern in Cents gespeichert: {Math.round(Number(value.monthly_price_cents) || 0)}</div>
            </div>
            <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Sortierung</Label>
                <Input type="number" min="0" value={value.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="admin-tariff-sort-input" />
            </div>
            <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Features (eine Zeile pro Eintrag)</Label>
                <Textarea
                    value={(value.features || []).join("\n")}
                    onChange={(e) => set("features", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                    rows={5}
                    placeholder={"Bis zu 30 aktive Inserate\nCSV-Export & API-Tokens"}
                    className="rounded-sm mt-1 text-sm font-mono-num"
                    data-testid="admin-tariff-features-input"
                />
            </div>
            <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!value.is_active} onChange={(e) => set("is_active", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" data-testid="admin-tariff-active-input" />
                    <span className="text-sm font-bold">Aktiv (für Händler sichtbar)</span>
                    {value.is_active ? <Eye className="w-3.5 h-3.5 text-green-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                </label>
            </div>
        </div>
    );
}
